import type { SimulationState, ThemeData, CompetitorProduct } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';
import { createLateCompetitor } from '../entities/CompetitorFactory';
import { generateProjectName } from '../entities/ProjectFactory';

export function competitorSystemUpdate(
  state: SimulationState,
  themeData: ThemeData,
  rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];

  // Check for late-entry competitors
  for (const def of themeData.entities.competitors) {
    if (def.startYear === state.company.currentYear && state.company.currentWeek === 1) {
      const alreadyExists = state.competitors.some((c) => c.id === def.id);
      if (!alreadyExists) {
        const newComp = createLateCompetitor(themeData, def.id, rng);
        if (newComp) {
          state.competitors.push(newComp);
        }
      }
    }
  }

  // Process each competitor
  for (const comp of state.competitors) {
    // Quality grows over time
    const yearsActive = state.company.currentYear - 1;
    const def = themeData.entities.competitors.find((c) => c.id === comp.id);
    if (def) {
      comp.quality = def.startQuality + yearsActive * def.growthRate;
    }

    // Release timer countdown
    comp.releaseTimer--;

    if (comp.releaseTimer <= 0) {
      // Competitor releases a product
      const isSpecialty = rng.nextBool(0.7);
      const categoryPool = isSpecialty
        ? themeData.entities.projectCategories.filter((c) => comp.specialties.includes(c.id))
        : themeData.entities.projectCategories;

      const category = categoryPool.length > 0
        ? rng.pick(categoryPool)
        : rng.pick(themeData.entities.projectCategories);

      // Pick a platform available at current year
      const availablePlatforms = themeData.entities.platforms.filter(
        (p) => p.releasedYear <= state.company.currentYear &&
          (!p.retiredYear || p.retiredYear > state.company.currentYear),
      );
      const platform = availablePlatforms.length > 0
        ? rng.pick(availablePlatforms)
        : themeData.entities.platforms[0]!;

      const productName = generateProjectName(rng, themeData);

      const product: CompetitorProduct = {
        name: productName,
        category: category.id,
        platform: platform.id,
        quality: comp.quality + rng.nextFloat(-10, 10),
        releaseYear: state.company.currentYear,
        releaseWeek: state.company.currentWeek,
        isActive: true,
        activeWeeksRemaining: 20,
      };

      comp.products.push(product);

      // Reset timer
      if (def) {
        const ticksPerWeek = themeData.balance.ticksPerDay * themeData.balance.daysPerWeek;
        comp.releaseTimer = rng.nextInt(
          def.releaseIntervalWeeks[0] * ticksPerWeek,
          def.releaseIntervalWeeks[1] * ticksPerWeek,
        );
      }

      events.push({
        type: 'COMPETITOR_RELEASED',
        competitorName: comp.name,
        productName: product.name,
        category: category.id,
        platform: platform.id,
      });
    }

    // Age out old competitor products
    const ticksPerWeek = themeData.balance.ticksPerDay * themeData.balance.daysPerWeek;
    if (state.company.currentTick % ticksPerWeek === 0) {
      for (const product of comp.products) {
        if (product.isActive) {
          product.activeWeeksRemaining--;
          if (product.activeWeeksRemaining <= 0) {
            product.isActive = false;
          }
        }
      }
    }

    // Keep only recent products (last 10)
    if (comp.products.length > 10) {
      comp.products = comp.products.slice(-10);
    }
  }

  return events;
}
